/**
 * Utility functions to detect sensitive information (PII/SPI) in text using validator.js
 * and libphonenumber-js.
 */

import { searchPhoneNumbersInText, type CountryCode } from 'libphonenumber-js';
import validator from 'validator';

export type SensitiveDataProtectionConfig = {
  enabled?: boolean;
  /** Countries whose national-format numbers are detected (default: primary markets IL/GB/US/AU + EU/EEA); international +... numbers are always checked */
  phoneCountries?: CountryCode[];
  /** validator.js isTaxID locales; an empty array makes the taxId check inert */
  taxIdLocales?: string[];
  protectedTypes?: {
    email?: boolean;
    creditCard?: boolean;
    phoneNumber?: boolean;
    ssn?: boolean;
    passport?: boolean;
    iban?: boolean;
    identityCard?: boolean;
    taxId?: boolean;
    secrets?: boolean;
    bankAccount?: boolean;
  };
};

export type ProtectedTypeKey = keyof NonNullable<SensitiveDataProtectionConfig['protectedTypes']>;

export type SensitiveDataMatch = {
  /** Analytics/i18n bucket. */
  category: ProtectedTypeKey;
  /** Specific rule that matched, e.g. 'email', 'phone', 'aws-access-key', 'au-tfn'. */
  ruleId: string;
  value: string;
  /** phone: ISO country of the number; taxId: the validator locale that matched; undefined otherwise */
  subtype?: string;
};

// EU-27 + EEA (Iceland, Liechtenstein, Norway). CountryCode (from libphonenumber-js) is
// the ISO-3166 enum; this is the GDPR-relevant subset.
const EU_EEA_COUNTRIES: CountryCode[] = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'IS',
  'LI',
  'NO',
];

// Primary markets first so a national-format number reports the right country: libphonenumber
// returns the first configured country whose plan accepts a bare number, and lenient plans
// (e.g. AT) would otherwise claim foreign numbers.
export const DEFAULT_PHONE_COUNTRIES: CountryCode[] = ['IL', 'GB', 'US', 'AU', ...EU_EEA_COUNTRIES];

// EU locales are deliberately opt-in per tenant: several accept plain 9-11 digit numbers,
// which would block booking refs / order numbers.
export const DEFAULT_TAX_ID_LOCALES = ['en-GB', 'en-US'];

type MatchDetail = { value: string; subtype?: string };
type ValidatorFunction = (args: {
  text: string;
  config: SensitiveDataProtectionConfig;
}) => MatchDetail | null;

// First substring matched by `regex` (must be global) that passes `isValid`, or null.
function firstMatch({
  text,
  regex,
  isValid,
}: {
  text: string;
  regex: RegExp;
  isValid: (candidate: string) => boolean;
}): MatchDetail | null {
  for (const match of text.matchAll(regex)) {
    if (isValid(match[0])) {
      return { value: match[0] };
    }
  }
  return null;
}

const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
const CREDIT_CARD_RE = /\b\d[\d\s-]{10,22}\d\b/g;
const SSN_RE = /\b\d{3}[-\s.]?\d{2}[-\s.]?\d{4}\b/g;
const PASSPORT_RE = /\b[A-Z]{1,2}\d{6,9}\b/g;
const IBAN_RE = /\b[A-Z]{2}\d{2}[A-Z0-9]{4,30}\b/gi;
const IDENTITY_CARD_RE = /\b\d{7,12}\b/g;
// isTaxID validates a single token, not free text, so candidates are extracted first.
// Case-sensitive (uppercase/digit runs only) so ordinary words never reach it.
const TAX_ID_CANDIDATE_RE = /\b[A-Z0-9]+(?:[ -][A-Z0-9]+)*\b/g;

// 9 digits, not a single repeated digit, and not the well-known placeholder.
function isPlausibleSsn(candidate: string): boolean {
  const digits = candidate.replace(/\D/g, '');
  return digits.length === 9 && !/^(\d)\1+$/.test(digits) && digits !== '123456789';
}

const findEmail: ValidatorFunction = ({ text }) =>
  firstMatch({ text, regex: EMAIL_RE, isValid: c => validator.isEmail(c) });

const findCreditCard: ValidatorFunction = ({ text }) =>
  firstMatch({
    text,
    regex: CREDIT_CARD_RE,
    isValid: c => validator.isCreditCard(c.replace(/[\s-]/g, '')),
  });

const findSsn: ValidatorFunction = ({ text }) =>
  firstMatch({ text, regex: SSN_RE, isValid: isPlausibleSsn });

const findPassport: ValidatorFunction = ({ text }) =>
  firstMatch({ text, regex: PASSPORT_RE, isValid: c => validator.isPassportNumber(c, 'any') });

const findIban: ValidatorFunction = ({ text }) =>
  firstMatch({ text, regex: IBAN_RE, isValid: c => validator.isIBAN(c.toUpperCase()) });

const findIdentityCard: ValidatorFunction = ({ text }) =>
  firstMatch({ text, regex: IDENTITY_CARD_RE, isValid: c => validator.isIdentityCard(c, 'any') });

// A day-first date leads with a zero and uses phone separators, so it clears the phone-like-lead
// guard below, and lenient national plans then accept it: `04.10.26` parses as a valid DE number
// (+4941026), `04.10.26 - 08.10.26` as a valid AT one. `isValid()` can't help — Germany really
// does have 5-digit local numbers. The ranges are what stop the mask eating a number that is
// merely date-shaped (`030-12-3456`), so they live in the pattern rather than a JS re-check; a
// real calendar parse would be wrong here, since a typo'd `31.02.26` is still not a phone number.
const DAY = String.raw`(?:0?[1-9]|[12]\d|3[01])`;
const MONTH = String.raw`(?:0?[1-9]|1[0-2])`;
const YEAR = String.raw`(?:(?:19|20)\d{2}|\d{2})`;
// Day-first (04.10.26, 4.10.2026) or ISO (2026-10-04) — the two shapes our locales produce. The
// back-reference makes both separators match, since a real date never mixes them. No lookbehind
// (browser compat): the preceding character is captured instead, so a `+` before the first group
// stops `+972-52-530` reading as a date.
//
// The trailing assertion has to tell a compact range (`04.10.26-08.10.26`) from a date-shaped
// prefix of a longer number (`04.12.34.5678`): both continue with a separator and digits, but only
// the range's continuation is itself separated. Masking the prefix would strand the rest and hide
// a real number from the sweep, so a continuation that ends in a bare digit run blocks the match.
const DATE_LIKE_RE = new RegExp(
  String.raw`(^|[^\d+])(?:${DAY}([./-])${MONTH}\2${YEAR}|(?:19|20)\d{2}([./-])${MONTH}\3${DAY})(?!\d)(?![./-]\d+(?![\d./-]))`,
  'g',
);

// Blanks date spans, padding with spaces so match offsets still index the original text.
function maskDates(text: string): string {
  return text.replace(
    DATE_LIKE_RE,
    (full, lead: string) => lead + ' '.repeat(full.length - lead.length),
  );
}

function findPhoneNumber({
  text,
  config,
}: {
  text: string;
  config: SensitiveDataProtectionConfig;
}): MatchDetail | null {
  const searchable = maskDates(text);
  // The undefined pass catches international +... numbers; each country pass adds its
  // national formats.
  const countries: (CountryCode | undefined)[] = [
    undefined,
    ...(config.phoneCountries ?? DEFAULT_PHONE_COUNTRIES),
  ];
  for (const country of countries) {
    for (const match of searchPhoneNumbersInText(searchable, { defaultCountry: country })) {
      const value = text.slice(match.startsAt, match.endsAt);
      // Precision guard: bare digit runs (dates, prices, order refs) parse as "valid"
      // numbers under lenient country plans. Require a phone-like lead — international +,
      // national trunk 0, or an area-code paren — to cut those false positives.
      if (!/^[+(0]/.test(value.trimStart())) {
        continue;
      }
      return { value, subtype: match.number.country };
    }
  }
  return null;
}

function findTaxId({
  text,
  config,
}: {
  text: string;
  config: SensitiveDataProtectionConfig;
}): MatchDetail | null {
  const locales = config.taxIdLocales ?? DEFAULT_TAX_ID_LOCALES;
  for (const match of text.matchAll(TAX_ID_CANDIDATE_RE)) {
    const stripped = match[0].replace(/[ -]/g, '');
    if (stripped.length < 6 || stripped.length > 20 || !/\d/.test(stripped)) {
      continue;
    }
    for (const locale of locales) {
      try {
        if (validator.isTaxID(stripped, locale)) {
          return { value: match[0], subtype: locale };
        }
      } catch {
        // locale not supported by this validator.js version — skip, don't throw
      }
    }
  }
  return null;
}

// taxId stays last so the specific validators above claim their formats before the generic
// tax-ID candidate sweep. `ruleId` is the stable slug reported to analytics per category.
const VALIDATORS: { category: ProtectedTypeKey; ruleId: string; validate: ValidatorFunction }[] = [
  { category: 'email', ruleId: 'email', validate: findEmail },
  { category: 'creditCard', ruleId: 'credit-card', validate: findCreditCard },
  { category: 'phoneNumber', ruleId: 'phone', validate: findPhoneNumber },
  { category: 'ssn', ruleId: 'ssn', validate: findSsn },
  { category: 'passport', ruleId: 'passport', validate: findPassport },
  { category: 'iban', ruleId: 'iban', validate: findIban },
  { category: 'identityCard', ruleId: 'identity-card', validate: findIdentityCard },
  { category: 'taxId', ruleId: 'tax-id', validate: findTaxId },
];

type RegexRule = { id: string; category: ProtectedTypeKey; regex: RegExp };

// Patterns validator.js/libphonenumber can't cover. Secret prefixes are case-SENSITIVE
// (AKIA, ghp_, sk-ant-) — do not add an `i` flag to them; only the AU keyword rules use it.
const REGEX_RULES: RegexRule[] = [
  { id: 'aws-access-key', category: 'secrets', regex: /\b(?:AKIA|ASIA|ABIA|ACCA)[A-Z0-9]{16}\b/ },
  {
    id: 'aws-secret-key',
    category: 'secrets',
    regex: /\b(?:aws_secret|secret[_-]?key)[=:\s]+["']?[A-Za-z0-9/+=]{40}["']?/i,
  },
  { id: 'github-token', category: 'secrets', regex: /\bgh[pousr]_[A-Za-z0-9]{36,255}\b/ },
  { id: 'github-pat', category: 'secrets', regex: /\bgithub_pat_[A-Za-z0-9_]{82}\b/ },
  // anthropic-key before openai-key: an sk-ant- key also matches the broader openai pattern.
  { id: 'anthropic-key', category: 'secrets', regex: /\bsk-ant-[A-Za-z0-9-]{20,}\b/ },
  { id: 'openai-key', category: 'secrets', regex: /\bsk-(?:proj-)?[A-Za-z0-9_-]{20,}\b/ },
  {
    id: 'stripe-key',
    category: 'secrets',
    regex: /\b(?:sk|pk|rk)_(?:live|test)_[A-Za-z0-9]{24,}\b/,
  },
  { id: 'slack-token', category: 'secrets', regex: /\bxox[baprs]-[A-Za-z0-9-]{10,}\b/ },
  { id: 'google-api-key', category: 'secrets', regex: /\bAIza[A-Za-z0-9_-]{35}\b/ },
  {
    id: 'jwt',
    category: 'secrets',
    regex: /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]*\b/,
  },
  {
    id: 'private-key',
    category: 'secrets',
    regex: /-----BEGIN\s+(?:RSA\s+|EC\s+|OPENSSH\s+)?PRIVATE KEY-----/,
  },
  {
    id: 'db-conn-string',
    category: 'secrets',
    regex: /\b(?:mongodb(?:\+srv)?|postgres(?:ql)?|mysql|redis|amqp):\/\/[^\s:]+:[^\s@]+@/,
  },
  { id: 'basic-auth-url', category: 'secrets', regex: /https?:\/\/[^\s:/]+:[^\s@/]+@/ },
  // Australia — validator.js has no en-AU; context-anchored to keep false positives low.
  {
    id: 'au-tfn',
    category: 'taxId',
    regex: /\b(?:TFN|Tax File)[:\s#]*\d{3}[\s-]?\d{3}[\s-]?\d{2,3}\b/i,
  },
  {
    id: 'au-medicare',
    category: 'identityCard',
    regex: /\b(?:Medicare)[:\s#]*[2-6]\d{3}[\s-]?\d{5}[\s-]?\d(?:[\s-/]?\d)?\b/i,
  },
  // BSB alone is a public branch code (not PII); only the BSB+account pair is blocked, as
  // anti-fraud financial-data hygiene. Never add a BSB-alone rule.
  { id: 'au-bsb-account', category: 'bankAccount', regex: /\b\d{3}-\d{3}[\s,]+\d{5,9}\b/ },
];

/**
 * Find the first piece of sensitive data in text based on configuration.
 * @returns The matched category, rule id, value, and subtype, or null if none found.
 */
export function findSensitiveData({
  text,
  config,
}: {
  text: string;
  config?: SensitiveDataProtectionConfig;
}): SensitiveDataMatch | null {
  if (config?.enabled === false) {
    return null;
  }

  const protectedTypes = config?.protectedTypes || {};

  // Specific regex rules run first: they are keyword-anchored or unique-prefix, so they
  // should win attribution over the permissive validated sweeps (e.g. a keyworded AU TFN
  // must report `au-tfn`, not the generic `tax-id` that also accepts any 9 digits).
  for (const rule of REGEX_RULES) {
    if (protectedTypes[rule.category] === false) {
      continue;
    }
    const match = rule.regex.exec(text);
    if (match) {
      return { category: rule.category, ruleId: rule.id, value: match[0] };
    }
  }

  for (const { category, ruleId, validate } of VALIDATORS) {
    if (protectedTypes[category] === false) {
      continue;
    }
    const detail = validate({ text, config: config ?? {} });
    if (detail) {
      return { category, ruleId, ...detail };
    }
  }

  return null;
}
